import { Loader2Icon } from 'lucide-react';
import { Suspense } from 'react';

import { usePullStatus } from '../hooks/use-pull-status';

import { ConflictsIndicator } from './conflicts-indicator';
import { DraftButton } from './draft-button';
import { MergeButton } from './merge-button';
import { MergedIndicator } from './merged-indicator';
import { ReopenButton } from './reopen-button';

import { ReviewButton } from '@/components/review-button';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { MergeStrategy } from '@/features/pulls/services/pulls';

export const PullActionButton = ({ openMergeDialog }: { openMergeDialog: (mergeStrategy: MergeStrategy) => void }) => {
  return (
    <Suspense fallback={<Skeleton className="h-6 w-36" />}>
      <PullActionButtonInner openMergeDialog={openMergeDialog} />
    </Suspense>
  );
};

export const PullActionButtonInner = ({
  openMergeDialog,
}: {
  openMergeDialog: (mergeStrategy: MergeStrategy) => void;
}) => {
  const { state, hasConflicts, isAuthor, alreadyReviewed, checksPassing, isApproved, pull } = usePullStatus();

  if (!pull) {
    return null;
  }

  if (state === 'MERGED') {
    return <MergedIndicator />;
  }

  if (state === 'DRAFT') {
    return <DraftButton pull={pull} />;
  }

  if (state === 'CLOSED') {
    return <ReopenButton pull={pull} />;
  }

  if (!isAuthor) {
    return (
      <ReviewButton
        alreadyReviewed={alreadyReviewed}
        pull={pull}
        openMergeDialog={openMergeDialog}
        hasConflicts={hasConflicts}
      />
    );
  }

  if (hasConflicts) {
    return <ConflictsIndicator />;
  }

  if (hasConflicts === undefined) {
    return (
      <Button variant="outline" size="sm" className="w-30" disabled>
        <Loader2Icon className="size-4 mr-2 animate-spin" />
      </Button>
    );
  }

  return (
    <MergeButton
      openMergeDialog={openMergeDialog}
      readyToMerge={checksPassing && isApproved}
      pull={pull}
      isAuthor={isAuthor}
    />
  );
};
